import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button-comp';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useUserStore, getCookie } from '@ekonavi/auth';
import { apiEndpoint } from '@/config';
import {
	CheckCircle,
	XCircle,
	Clock,
	Users,
	FileText,
	Eye,
	Award,
	Shield,
	AlertCircle
} from 'lucide-react';
import { RoleRequestApproval } from '@/components/workflows/RoleRequestApproval';
import { AssignmentManagement } from '@/components/workflows/AssignmentManagement';
import { MethodologyApproval } from '@/components/workflows/MethodologyApproval';

interface RoleRequest {
	id: string;
	user_id: string;
	role_type: string;
	status: string;
	created_at: string;
	experience_description: string;
	certifications?: string;
	user?: {
		name: string;
		email: string;
	};
}

interface Assignment {
	id: string;
	type: string;
	status: string;
	created_at: string;
	report_id?: string;
	verifier_id?: string;
	valuator_id?: string;
	certifier_id?: string;
	report?: {
		title: string;
		producer?: {
			name: string;
		};
	};
}

interface Methodology {
	id: string;
	name: string;
	description: string;
	status: string;
	created_at: string;
	creator?: {
		name: string;
	};
}

export default function Workflows() {
	const { user } = useUserStore();
	const authToken = getCookie('auth_token');
	const authHeaders = authToken ? { 'Authorization': `Bearer ${authToken}` } : undefined;
	const [roleRequests, setRoleRequests] = useState<RoleRequest[]>([]);
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [methodologies, setMethodologies] = useState<Methodology[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [activeTab, setActiveTab] = useState('role-requests');

	// Fetch all workflow data
	useEffect(() => {
		const fetchWorkflowData = async () => {
			if (!authHeaders) return;

			try {
				setIsLoading(true);

				// Fetch role requests
				const roleRequestsResponse = await fetch(`${apiEndpoint}/requests`, {
					headers: authHeaders
				});
				if (roleRequestsResponse.ok) {
					const roleRequestsData = await roleRequestsResponse.json();
					setRoleRequests(roleRequestsData);
				}

				// Fetch assignments (verifications, valuations, certifications)
				const [verificationsRes, valuationsRes, certificationsRes] = await Promise.all([
					fetch(`${apiEndpoint}/verifications`, { headers: authHeaders }),
					fetch(`${apiEndpoint}/valuations`, { headers: authHeaders }),
					fetch(`${apiEndpoint}/certifications`, { headers: authHeaders })
				]);

				const allAssignments: Assignment[] = [];

				if (verificationsRes.ok) {
					const verifications = await verificationsRes.json();
					verifications.forEach((v: any) => {
						allAssignments.push({
							id: v.id,
							type: 'verification',
							status: v.status,
							created_at: v.created_at,
							report_id: v.report_id,
							verifier_id: v.verifier_id,
							report: v.report
						});
					});
				}

				if (valuationsRes.ok) {
					const valuations = await valuationsRes.json();
					valuations.forEach((v: any) => {
						allAssignments.push({
							id: v.id,
							type: 'valuation',
							status: v.status,
							created_at: v.created_at,
							report_id: v.report_id,
							valuator_id: v.valuator_id,
							report: v.report
						});
					});
				}

				if (certificationsRes.ok) {
					const certifications = await certificationsRes.json();
					certifications.forEach((c: any) => {
						allAssignments.push({
							id: c.id,
							type: 'certification',
							status: c.status,
							created_at: c.created_at,
							report_id: c.report_id,
							certifier_id: c.certifier_id,
							report: c.report
						});
					});
				}

				setAssignments(allAssignments.sort((a, b) =>
					new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
				));

				// Fetch methodologies
				const methodologiesResponse = await fetch(`${apiEndpoint}/methodologies`, {
					headers: authHeaders
				});
				if (methodologiesResponse.ok) {
					const methodologiesData = await methodologiesResponse.json();
					setMethodologies(methodologiesData);
				}

			} catch (error) {
				console.error('Error fetching workflow data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchWorkflowData();
	}, [authHeaders]);

	const getStatusBadge = (status: string) => {
		switch (status) {
			case 'pending':
				return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
			case 'approved':
				return <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Aprovado</Badge>;
			case 'rejected':
				return <Badge variant="outline" className="text-red-600 border-red-300"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>;
			case 'assigned':
				return <Badge variant="outline" className="text-blue-600 border-blue-300"><Clock className="w-3 h-3 mr-1" />Atribuído</Badge>;
			case 'in_progress':
				return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="w-3 h-3 mr-1" />Em Progresso</Badge>;
			case 'completed':
				return <Badge variant="outline" className="text-green-600 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Concluído</Badge>;
			case 'issued':
				return <Badge variant="outline" className="text-emerald-600 border-emerald-300"><CheckCircle className="w-3 h-3 mr-1" />Emitido</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};

	const getRoleIcon = (roleType: string) => {
		switch (roleType) {
			case 'verifier': return <Eye className="w-4 h-4" />;
			case 'valuator': return <Award className="w-4 h-4" />;
			case 'certifier': return <Shield className="w-4 h-4" />;
			default: return <Users className="w-4 h-4" />;
		}
	};

	const getAssignmentIcon = (type: string) => {
		switch (type) {
			case 'verification': return <Eye className="w-4 h-4" />;
			case 'valuation': return <Award className="w-4 h-4" />;
			case 'certification': return <Shield className="w-4 h-4" />;
			default: return <FileText className="w-4 h-4" />;
		}
	};

	const getRoleLabel = (roleType: string) => {
		switch (roleType) {
			case 'verifier': return 'Verificador';
			case 'valuator': return 'Avaliador';
			case 'certifier': return 'Certificador';
			default: return roleType;
		}
	};

	const getAssignmentLabel = (type: string) => {
		switch (type) {
			case 'verification': return 'Verificação';
			case 'valuation': return 'Avaliação';
			case 'certification': return 'Certificação';
			default: return type;
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
					<p className="text-muted-foreground">Carregando fluxos de trabalho...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold">Fluxos de Trabalho</h1>
				<p className="text-muted-foreground mt-1">
					Gerencie aprovações de funções, atribuições e metodologias
				</p>
			</div>

			{/* Summary Cards */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
				<Card>
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-orange-100 rounded-lg">
								<Clock className="w-6 h-6 text-orange-600" />
							</div>
							<div>
								<p className="text-sm font-medium text-muted-foreground">Pendentes</p>
								<p className="text-2xl font-bold">
									{roleRequests.filter(r => r.status === 'pending').length}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-blue-100 rounded-lg">
								<Users className="w-6 h-6 text-blue-600" />
							</div>
							<div>
								<p className="text-sm font-medium text-muted-foreground">Funções</p>
								<p className="text-2xl font-bold">{roleRequests.length}</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-green-100 rounded-lg">
								<FileText className="w-6 h-6 text-green-600" />
							</div>
							<div>
								<p className="text-sm font-medium text-muted-foreground">Atribuições</p>
								<p className="text-2xl font-bold">{assignments.length}</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="p-2 bg-purple-100 rounded-lg">
								<AlertCircle className="w-6 h-6 text-purple-600" />
							</div>
							<div>
								<p className="text-sm font-medium text-muted-foreground">Metodologias</p>
								<p className="text-2xl font-bold">{methodologies.length}</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Tab Navigation */}
			<div className="border-b">
				<div className="flex space-x-8">
					<button
						onClick={() => setActiveTab('role-requests')}
						className={`py-2 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'role-requests'
								? 'border-primary text-primary'
								: 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
						}`}
					>
						Solicitações de Função
					</button>
					<button
						onClick={() => setActiveTab('assignments')}
						className={`py-2 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'assignments'
								? 'border-primary text-primary'
								: 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
						}`}
					>
						Atribuições
					</button>
					<button
						onClick={() => setActiveTab('methodologies')}
						className={`py-2 px-1 border-b-2 font-medium text-sm ${
							activeTab === 'methodologies'
								? 'border-primary text-primary'
								: 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
						}`}
					>
						Metodologias
					</button>
				</div>
			</div>

			{/* Tab Content */}
			{activeTab === 'role-requests' && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Users className="w-5 h-5" />
							Solicitações de Função
						</CardTitle>
						<CardDescription>
							Aprove ou rejeite solicitações para se tornar verificador, avaliador ou certificador
						</CardDescription>
					</CardHeader>
					<CardContent>
						<RoleRequestApproval
							roleRequests={roleRequests}
							onUpdate={() => window.location.reload()}
						/>
					</CardContent>
				</Card>
			)}

			{activeTab === 'assignments' && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<FileText className="w-5 h-5" />
							Gerenciamento de Atribuições
						</CardTitle>
						<CardDescription>
							Monitore e gerencie atribuições de verificação, avaliação e certificação
						</CardDescription>
					</CardHeader>
					<CardContent>
						<AssignmentManagement
							assignments={assignments}
							onUpdate={() => window.location.reload()}
						/>
					</CardContent>
				</Card>
			)}

			{activeTab === 'methodologies' && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<AlertCircle className="w-5 h-5" />
							Aprovação de Metodologias
						</CardTitle>
						<CardDescription>
							Aprove novas metodologias ESG para uso na plataforma
						</CardDescription>
					</CardHeader>
					<CardContent>
						<MethodologyApproval
							methodologies={methodologies}
							onUpdate={() => window.location.reload()}
						/>
					</CardContent>
				</Card>
			)}
		</div>
	);
}